/**
 * `buildBundleIndexes` — a validated pack's lookup maps, built once
 * (architecture.md §6 step 5).
 *
 * @remarks
 * It lives HERE, beside the {@link BundleIndexes} type it produces, and that
 * placement is the point. D20 gave the engine the bundle PORT — the index
 * shapes are the engine's view of the world — but left the BUILDER in the
 * loader, and it was then re-implemented twice more (the shared fixture
 * builder, and engine-runtime's own test bundle) because neither of those may
 * import the loader: `content-loader` depends on `engine-runtime` for the type,
 * so the arrow cannot be reversed.
 *
 * Three copies of one loop is the DRY contract's "mandate to extract" (D50
 * added a field and had to add it three times, which is how the smell surfaced).
 * The function is pure — a total map from `ContentPackDefinition` to lookup
 * tables, no I/O, no clock — so it belongs in the isomorphic core, and every
 * package that needs it already depends on this one.
 *
 * Deterministic by construction: map insertion order follows pack declaration
 * order, which is what keeps catalog derivation and every walk over these maps
 * replayable.
 *
 * @packageDocumentation
 */

import {
	type ActorDefinition,
	type ActorId,
	type ConditionDefinition,
	containerActorIdOf,
	containerItemIdOf,
	type ContentPackDefinition,
	type ExitId,
	isTopicIdKey,
	type ItemDefinition,
	type ItemId,
	type LexiconDefinition,
	type RoomDefinition,
	type RoomId,
} from '@orpheus/content-schema';
import { wordsOfProse } from '@orpheus/parser-engine';

import type { BundleIndexes, IndexedExit, LivePage, LivePageReading } from './engine-game-bundle.js';

import { DOOR_STATE_WORDS } from '../entities/door-state-words.js';
import { packVerbWordsOf } from './pack-verb-words.js';

/**
 * The keys under which a definition speaks: a description in either of its
 * forms (`briefDescription`, `description`), a line's `text`, a conditional
 * text's `fallback`. Everything else on a definition is a name, an id, a
 * condition or a number — never a word the player read.
 */
const PROSE_KEYS: ReadonlySet<string> = new Set(['briefDescription', 'description', 'fallback', 'text']);

/**
 * Every string a definition speaks, however deep — its description in every
 * reading, its fragments, its arrival lines, its answers, its refusals.
 *
 * @remarks
 * A structural walk rather than a field list, so a prose field the schema
 * grows next month is on the page the day it is authored, and a field the
 * audit's roster reads is one this walk reads (the roster is the typed
 * statement of the same fact, and lives where the audit can import it).
 *
 * @param node - A definition, or any part of one
 * @param spoken - The strings found so far (appended in place)
 * @returns The strings, in document order
 */
const spokenStringsOf = (node: unknown, spoken: string[] = []): readonly string[] => {
	if (Array.isArray(node)) {
		for (const member of node) {
			spokenStringsOf(member, spoken);
		}
	} else if (typeof node === 'object' && node !== null) {
		for (const [key, value] of Object.entries(node)) {
			if (typeof value === 'string') {
				if (PROSE_KEYS.has(key)) {
					spoken.push(value);
				}
			} else {
				spokenStringsOf(value, spoken);
			}
		}
	}

	return spoken;
};

/**
 * D205 (widened by D208): every distinct word a thing's own PAGE could ever
 * speak — folded by the parser's own stages so the words meet a player's
 * typed modifier on equal terms. D205 read the description and its
 * fragments; Calder's corpus (D206) showed the player reads the arrival
 * line, the ask answers and the refusals the same way ("wet ash" is said
 * on arrival and nowhere else), so the page is every line the thing itself
 * speaks. Static on purpose: a word from a line the player has not yet
 * heard can only ever select the SAME entity, and reading all of them once
 * at load is what keeps the per-turn catalog a projection rather than a
 * second prose resolver.
 *
 * @param definition - A room, an item or (D232) an actor, whole
 * @returns The folded words, each once, in first-appearance order
 */
const pageWordsOf = (definition: ActorDefinition | ItemDefinition | RoomDefinition): readonly string[] => [
	...new Set(spokenStringsOf(definition).flatMap(wordsOfProse)),
];

/**
 * Is this node a piece of conditional text in its object form — a fallback
 * and ordered variants (D55)? The test is the text's own shape, a string
 * fallback and a string on every variant, because rooms carry other
 * fallback-and-variants declarations (a plate, an ambience bed) whose
 * readings are ids and settings, never words.
 *
 * @param node - Any part of a definition
 * @returns Whether its readings differ by the world's state
 */
const isConditionalTextNode = (
	node: object,
): node is {
	readonly fallback: string;
	readonly variants: readonly { readonly condition: ConditionDefinition; readonly text: string }[];
} =>
	'fallback' in node &&
	typeof node.fallback === 'string' &&
	'variants' in node &&
	Array.isArray(node.variants) &&
	node.variants.every((variant: unknown) => typeof variant === 'object' && variant !== null && 'text' in variant && typeof variant.text === 'string');

/**
 * D216: a definition's page split by whether the world can change it — the
 * words every reading says, and each conditional text's readings apart.
 *
 * @remarks
 * D205 folded every reading of a description into one list, on the argument
 * that a word from a line the player has not heard yet can only select the
 * SAME thing. That holds for a word only one thing uses, and it fails for the
 * words two things take turns to say: both lockers read "standing open" once
 * their doors are, and "shut" before, so with every reading folded both
 * answered `open locker` and `shut locker` at all times, and state could
 * never tell them apart. A reading is now judged when it is showing. What
 * every reading of a text says is still said always — only the words that
 * change with the world wait for it.
 *
 * @param definition - A room, an item or (D232) an actor, whole
 * @returns Its steady words and its conditional readings
 */
const livePageOf = (definition: ActorDefinition | ItemDefinition | RoomDefinition): LivePage => {
	const steadyStrings: string[] = [];
	const readings: LivePageReading[] = [];

	const walk = (node: unknown): void => {
		if (Array.isArray(node)) {
			for (const member of node) {
				walk(member);
			}
		} else if (typeof node === 'object' && node !== null) {
			if (isConditionalTextNode(node)) {
				const fallbackWords = [...new Set(wordsOfProse(node.fallback))];
				const variants = node.variants.map((variant) => ({
					condition: variant.condition,
					words: [...new Set(wordsOfProse(variant.text))],
				}));
				const everyReadingSays = fallbackWords.filter((word) => variants.every((variant) => variant.words.includes(word)));

				steadyStrings.push(everyReadingSays.join(' '));
				readings.push({ fallbackWords, variants });

				return;
			}

			for (const [key, value] of Object.entries(node)) {
				if (typeof value === 'string') {
					if (PROSE_KEYS.has(key)) {
						steadyStrings.push(value);
					}
				} else {
					walk(value);
				}
			}
		}
	};

	walk(definition);

	return { readings, steadyWords: [...new Set(steadyStrings.flatMap(wordsOfProse))]};
};

/**
 * D211: every word a pack declares a thing by, folded as player input is
 * (the same `wordsOfProse` the page words go through, so "sea-grape" and
 * "Sea-Grape" meet as one word). Items contribute their name, nouns,
 * aliases and adjectives; rooms their name and nouns. Not the page words —
 * those are legal only for the entity that speaks them (D208), and a
 * pack-wide set of them would make every word of every description
 * uncorrectable everywhere.
 *
 * @param contentPack - The parsed pack
 * @returns The words, each once
 */
const declaredWordsOf = (contentPack: ContentPackDefinition): ReadonlySet<string> =>
	new Set(
		[
			...contentPack.items.flatMap((item) => [
				item.name,
				...item.nouns,
				...item.aliases,
				...item.adjectives,
				// D216: a door's words are declared by having the door — `opened` is a word wherever a thing opens.
				...(item.openable === undefined ? [] : DOOR_STATE_WORDS),
			]),
			...contentPack.rooms.flatMap((room) => [room.name, ...room.nouns]),
			// D232: an actor is declared by its words exactly as an item is.
			...contentPack.actors.flatMap((actor) => [actor.name, ...actor.nouns, ...actor.aliases, ...actor.adjectives]),
			// D231: a topic's plain keys are words the pack declares a subject by
			// — askable, so never a typo to correct into something else.
			...plainTopicKeysOf(contentPack),
		].flatMap(wordsOfProse),
	);

/**
 * D231: every plain (non-id) topic key in the pack — items', rooms' and
 * (D232) actors' — as authored — the subjects the pack declares that no thing is named for.
 *
 * @param contentPack - The parsed pack
 * @returns The keys, in document order
 */
const plainTopicKeysOf = (contentPack: ContentPackDefinition): readonly string[] =>
	[...contentPack.items, ...contentPack.rooms, ...contentPack.actors].flatMap((definition) =>
		(definition.topics ?? []).flatMap((topic) => topic.keys.filter((key) => !isTopicIdKey(key))),
	);

/**
 * D227: every name of two or more words the pack declares a thing by, as
 * authored — the parser normalizes them as it normalizes its catalog's names,
 * so one rule reads both sides. Adjectives are not names and are not here.
 *
 * @param contentPack - The parsed pack
 * @returns The multiword names, each once
 */
const claimedNamesOf = (contentPack: ContentPackDefinition): ReadonlySet<string> =>
	new Set(
		[
			...contentPack.items.flatMap((item) => [item.name, ...item.nouns, ...item.aliases]),
			...contentPack.rooms.flatMap((room) => [room.name, ...room.nouns]),
			// D232: an actor's multiword names are claimed as an item's are.
			...contentPack.actors.flatMap((actor) => [actor.name, ...actor.nouns, ...actor.aliases]),
			// D231: a multiword topic key is a name the pack claims for a subject.
			...plainTopicKeysOf(contentPack),
		].filter((name) => name.trim().includes(' ')),
	);

/**
 * D212: every word the pack speaks anywhere — the prose walk over the WHOLE
 * pack, not one definition — beside every word it declares a thing by. The
 * pack's own words are the one dictionary it is honest to consult: a word
 * the game uses is a word its players will type.
 *
 * @param contentPack - The parsed pack
 * @param declaredWords - The declared words, already folded
 * @returns The union, each word once
 */
const knownWordsOf = (contentPack: ContentPackDefinition, declaredWords: ReadonlySet<string>): ReadonlySet<string> =>
	new Set([...declaredWords, ...spokenStringsOf(contentPack).flatMap(wordsOfProse)]);

/**
 * Build every index from a validated pack.
 *
 * @param contentPack - The parsed, semantically-validated pack
 * @param baseLexicon - D229: the base vocabulary the pack extends, when the caller has it — the static walks do not, and read no HELP
 * @returns The bundle's indexes, in pack declaration order
 */
export const buildBundleIndexes = (contentPack: ContentPackDefinition, baseLexicon?: LexiconDefinition): BundleIndexes => {
	const declaredWords = declaredWordsOf(contentPack);
	const exitsById = new Map<ExitId, IndexedExit>();
	const initialItemIdsByRoomId = new Map<RoomId, readonly ItemId[]>();
	const roomsById = new Map<RoomId, RoomDefinition>();

	for (const room of contentPack.rooms) {
		roomsById.set(room.roomId, room);
		initialItemIdsByRoomId.set(room.roomId, room.itemPlacements);

		for (const exit of room.exits) {
			exitsById.set(exit.exitId, { exit, owningRoomId: room.roomId });
		}
	}

	// D50: the reverse view of containment. Authored on the CONTAINED item —
	// one field per item makes "in exactly one container" true by construction —
	// so the container's side is derived here rather than every turn.
	// D240: a body's pockets are the same reverse view, keyed by the body.
	const containedItemIdsByContainerId = new Map<ItemId, ItemId[]>();
	const carriedItemIdsByActorId = new Map<ActorId, ItemId[]>();

	for (const item of contentPack.items) {
		const containerItemId = containerItemIdOf(item.containedIn);
		const carryingActorId = containerActorIdOf(item.containedIn);

		if (containerItemId !== undefined) {
			containedItemIdsByContainerId.set(containerItemId, [...(containedItemIdsByContainerId.get(containerItemId) ?? []), item.itemId]);
		}

		if (carryingActorId !== undefined) {
			carriedItemIdsByActorId.set(carryingActorId, [...(carriedItemIdsByActorId.get(carryingActorId) ?? []), item.itemId]);
		}
	}

	return {
		// D232: the actor table's lookup — the catalog, the handlers and the conditions read it per turn.
		actorsById: new Map(contentPack.actors.map((actor) => [actor.actorId, actor])),
		// R35: the audio table's lookup — `resolveRoomAmbience` reads it per turn.
		audioById: new Map(contentPack.audio.map((audio) => [audio.audioId, audio])),
		carriedItemIdsByActorId,
		claimedNames: claimedNamesOf(contentPack),
		containedItemIdsByContainerId,
		counterDefinitionsById: new Map(contentPack.counters.map((counter) => [counter.counterId, counter])),
		declaredWords,
		descriptionWordsByActorId: new Map(contentPack.actors.map((actor) => [actor.actorId, pageWordsOf(actor)])),
		descriptionWordsByItemId: new Map(contentPack.items.map((item) => [item.itemId, pageWordsOf(item)])),
		descriptionWordsByRoomId: new Map(contentPack.rooms.map((room) => [room.roomId, pageWordsOf(room)])),
		endingsById: new Map(contentPack.endings.map((ending) => [ending.endingId, ending])),
		eventsById: new Map(contentPack.events.map((event) => [event.eventId, event])),
		exitsById,
		flagDefinitionsById: new Map(contentPack.flags.map((flag) => [flag.flagId, flag])),
		imagesById: new Map(contentPack.images.map((image) => [image.imageId, image])),
		initialItemIdsByRoomId,
		itemsById: new Map(contentPack.items.map((item) => [item.itemId, item])),
		knownWords: knownWordsOf(contentPack, declaredWords),
		livePageByActorId: new Map(contentPack.actors.map((actor) => [actor.actorId, livePageOf(actor)])),
		livePageByItemId: new Map(contentPack.items.map((item) => [item.itemId, livePageOf(item)])),
		livePageByRoomId: new Map(contentPack.rooms.map((room) => [room.roomId, livePageOf(room)])),
		// D229 (E15): what the pack teaches beyond the base, for HELP; the base is the loader's to hand over.
		packVerbWords: packVerbWordsOf(contentPack.vocabulary, baseLexicon),
		roomsById,
		// R32: the sprite table's lookup — `resolveRoomSprites` reads it per turn.
		spritesById: new Map(contentPack.sprites.map((sprite) => [sprite.spriteId, sprite])),
		voiceLinesById: new Map(contentPack.voiceLines.map((voiceLine) => [voiceLine.voiceLineId, voiceLine])),
	};
};
